/**
 * A tenant's console identity, as CSS custom properties and as JSON.
 *
 * In `src/shared/` because BOTH sides need the same answer: `src/app/root.tsx`
 * server-renders it into `<head>`, and `GET /api/branding` hands the identical
 * values to the React Native apps, which cannot import from `src/app` (Apps CI
 * installs only inside `apps/`, so root `node_modules` does not exist there).
 * Two implementations of a CONTRAST GUARANTEE would be two things to prove and
 * one of them would drift silently — surfacing only as an unreadable screen on
 * a cleaner's phone. One implementation, served.
 */

import { deriveIdentityRamp, type IdentityRamp, type Substrate } from './ramp';

/**
 * Bumped whenever the token NAMES or their meaning change.
 *
 * A shipped mobile binary caches the ramp it was given. It compares this before
 * trusting the cache, and falls back to its built-in neutral theme when it does
 * not recognise the version — so an old app on a new server degrades to
 * Broomstack's palette rather than to a half-applied one.
 */
export const CONSOLE_RAMP_VERSION = 1;

export interface ConsoleRamp {
  /** Derived from the tenant's primary. Carries the identity everywhere. */
  id: IdentityRamp;
  /**
   * Derived from the tenant's accent. A second, quieter identity for the places
   * that need to differ from the primary without becoming a status colour.
   *
   * `secondary` deliberately gets NO ramp: three full families is 36 tokens, and
   * nothing in the console consumes a third. It seeds chart series and
   * decorative fills only, where the raw value is read directly.
   */
  accent: IdentityRamp;
}

/**
 * Small memo. The derivation runs ~70 OKLCH round trips and a handful of
 * bisections — sub-millisecond, but it runs on EVERY server-rendered request,
 * and the input space is "the number of tenants", not "the number of requests".
 */
const CACHE = new Map<string, ConsoleRamp>();
const CACHE_MAX = 64;

export function deriveConsoleRamp(
  primary: string,
  accent: string,
  substrate?: Substrate,
): ConsoleRamp {
  const key = `${primary}|${accent}|${substrate ? JSON.stringify(substrate) : ''}`;
  const hit = CACHE.get(key);
  if (hit) return hit;

  const ramp: ConsoleRamp = {
    id: deriveIdentityRamp(primary, substrate),
    accent: deriveIdentityRamp(accent, substrate),
  };

  // Oldest-first eviction. A Map iterates in insertion order, so this is enough
  // without tracking access times — tenants are few and long-lived.
  if (CACHE.size >= CACHE_MAX) {
    const oldest = CACHE.keys().next();
    if (!oldest.done) CACHE.delete(oldest.value);
  }
  CACHE.set(key, ramp);
  return ramp;
}

/** kebab-case the camelCase slot names, so `groundHover` → `--id-ground-hover`. */
function tokenName(prefix: string, slot: string): string {
  return `--${prefix}-${slot.replace(/[A-Z]/g, (ch) => `-${ch.toLowerCase()}`)}`;
}

/**
 * The `<style>` body that applies a tenant's identity to the console and crew
 * portal, server-rendered into `<head>`.
 *
 * ⚠️ EVERY PROPERTY THIS EMITS IS NAMESPACED `--id-*` OR `--id-accent-*`, AND
 * THAT IS LOAD-BEARING.
 *
 * The reverted attempt emitted `--admin-primary` and `--admin-ink-strong`, which
 * `admin.css` read for its own palette — so a customer's hex reached the
 * console's ink and chrome and repainted the whole back office. A separate
 * namespace means a tenant value can only ever land where someone deliberately
 * wrote `var(--id-…)`, and `tests/unit/css-tokens-defined.test.ts` asserts this
 * function never emits anything declared in `admin.css`'s own `:root`.
 *
 * Reusing `--primary` would have been the smaller diff. It is also exactly how
 * the last one happened: the name carried the authority.
 *
 * Every value is `#rrggbb`, proved by `ramp.test.ts`, so this cannot inject.
 */
export function consoleThemeStyleBlock(ramp: ConsoleRamp): string {
  const out: string[] = [':root{'];
  for (const [slot, value] of Object.entries(ramp.id)) {
    out.push(`${tokenName('id', slot)}:${value};`);
  }
  for (const [slot, value] of Object.entries(ramp.accent)) {
    out.push(`${tokenName('id-accent', slot)}:${value};`);
  }
  out.push('}');
  return out.join('');
}

/**
 * The wire shape for `GET /api/branding` — flat, so a mobile client can map it
 * onto its own token names without walking a tree.
 */
export function flattenConsoleRamp(ramp: ConsoleRamp): Record<string, string> {
  const flat: Record<string, string> = {};
  for (const [slot, value] of Object.entries(ramp.id)) flat[slot] = value;
  for (const [slot, value] of Object.entries(ramp.accent)) {
    flat[`accent${slot[0]!.toUpperCase()}${slot.slice(1)}`] = value;
  }
  return flat;
}
